import {
	IProduct,
	IProductVariant,
} from '@/interfaces/api/product/IProductResponse';

// Helper to build variant type string
function buildTypeString(record: any): string {
	let type = record['Option1 Name'] || '';
	if (record['Option2 Name']) type += ` - ${record['Option2 Name']}`;
	if (record['Option3 Name']) type += ` - ${record['Option3 Name']}`;
	return type;
}

// Helper to build variant name string
function getVariantName(record: any): string {
	let name = record['Option1 Value'] || '';
	if (name && record['Option2 Value']) name += ` - ${record['Option2 Value']}`;
	if (name && record['Option3 Value']) name += ` - ${record['Option3 Value']}`;
	return name;
}

// Helper to get variant type (look back if missing)
function getVariantType(records: any[], currentRow: number): string {
	let type = records[currentRow]['Option1 Name'];
	if (!type) {
		for (let i = currentRow - 1; i >= 0; i--) {
			if (records[i]['Option1 Name']) {
				type = buildTypeString(records[i]);
				break;
			}
		}
	} else {
		type = buildTypeString(records[currentRow]);
	}
	return type || '';
}

// Map a single variant from CSV
function mapVariantFromCsv(
	records: any[],
	currentRow: number,
): Partial<IProductVariant> | null {
	const handle = records[currentRow]['Handle'];
	if (!handle) return null;
	const type = getVariantType(records, currentRow);
	const name = getVariantName(records[currentRow]);
	return {
		handle,
		type,
		name,
		sku: records[currentRow]['Variant SKU'],
		grams: records[currentRow]['Variant Grams']
			? Number(records[currentRow]['Variant Grams'])
			: 0,
		inventoryQty: records[currentRow]['Variant Inventory Qty']
			? Number(records[currentRow]['Variant Inventory Qty'])
			: 0,
		price: records[currentRow]['Variant Price']
			? String(records[currentRow]['Variant Price'])
			: '',
		requiresShipping:
			records[currentRow]['Variant Requires Shipping'] === 'true' ||
			records[currentRow]['Variant Requires Shipping'] === true,
		barcode: records[currentRow]['Variant Barcode'] || '',
		image:
			records[currentRow]['Image Src'] ||
			records[currentRow]['Variant Image'] ||
			'',
		weightUnit: records[currentRow]['Variant Weight Unit'] || '',
		productInfo: records[currentRow]['Product Info'] || null,
		id: Date.now() + Math.random(), // Temporary unique id
		userId: '',
		createdAt: '',
		updatedAt: '',
		deletedAt: '',
		uuid: '',
	};
}

// Main mapping function
export function mapCsvToProducts(records: any[]): IProduct[] {
	const productsMap = new Map<string, IProduct>();

	for (let row = 0; row < records.length; row++) {
		const handle = records[row]['Handle'];
		if (!handle) continue;

		// If product not already mapped and has title
		if (!productsMap.has(handle) && records[row]['Title']) {
			productsMap.set(handle, {
				id: Date.now() + Math.random(), // Temporary unique id
				userId: '',
				handle,
				title: records[row]['Title'],
				bodyHtml: records[row]['Body (HTML)'] || '',
				productInfo: records[row]['Additional Info'] || null,
				vendor: records[row]['Vendor'] || '',
				type: records[row]['Type'] || '',
				tags: records[row]['Tags'] || '',
				published:
					records[row]['Published'] === 'true' ||
					records[row]['Published'] === true,
				imageSrc: records[row]['Image Src'] || '',
				variants: [],
				uuid: '',
				createdAt: '',
				updatedAt: '',
				deletedAt: '',
			});
		}

		// Map variant if SKU exists
		const variant = mapVariantFromCsv(records, row);
		if (variant && productsMap.has(handle)) {
			productsMap.get(handle)!.variants.push(variant as IProductVariant);
		}
	}

	return Array.from(productsMap.values());
}
